// overlayShades.js
//
// Two shades of ONE colour, for a study that draws two adjacent bands.
//
// The Retro-Complement study shades a retrograde loop and the direct retrace that undoes it, side
// by side. They are two halves of one relationship, so they have to read as one colour at two
// strengths — taking a second entry from the overlay palette would draw them as two unrelated
// studies that happen to be adjacent, which is the opposite of what the pairing says.
//
// The stronger shade goes on the retrace, because that is the half the summary measures. A reader
// glancing at the chart should land on the period the numbers describe.

/** The retrograde loop: present, but plainly the reference half. */
export const LOOP_ALPHA = 0.22;

/** The direct retrace: the half the study is about, and the half the statistics cover. */
export const RETRACE_ALPHA = 0.55;

/**
 * `color` with its alpha replaced.
 *
 * Handles the three forms the palette and the pickers actually produce — `rgba()`, `rgb()` and
 * `#rrggbb`/`#rgb`. Anything else is returned UNCHANGED rather than rejected: a named colour like
 * `teal` is still a colour, and a band drawn at the wrong opacity is a far smaller failure than a
 * band not drawn at all.
 *
 * @param {string} color
 * @param {number} alpha 0..1
 * @returns {string}
 */
export function withAlpha(color, alpha) {
  if (typeof color !== 'string' || !color.trim()) return color;
  const a = Math.max(0, Math.min(1, Number(alpha)));
  if (!Number.isFinite(a)) return color;

  const rgb = color.match(/^\s*rgba?\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)\s*(?:,\s*[\d.]+\s*)?\)\s*$/i);
  if (rgb) return `rgba(${rgb[1]}, ${rgb[2]}, ${rgb[3]}, ${a})`;

  const hex = color.trim().match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i);
  if (hex) {
    const h = hex[1].length === 3 ? hex[1].split('').map(c => c + c).join('') : hex[1];
    const n = parseInt(h, 16);
    /* eslint-disable no-bitwise */
    return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${a})`;
    /* eslint-enable no-bitwise */
  }
  return color;
}

/**
 * The two shades a complement study draws, from the one colour the dialog holds.
 *
 * @param {string} color
 * @returns {{loop: string, retrace: string}}
 */
export function complementShades(color) {
  return { loop: withAlpha(color, LOOP_ALPHA), retrace: withAlpha(color, RETRACE_ALPHA) };
}
